import React, { useState, useEffect, useCallback } from 'react';
import mammoth from 'mammoth';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Fade,
  Collapse,
  Card,
  CardContent,
  Divider,
  Badge
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Description as DocumentIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Article as DocIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as PreviewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';

interface DesignChangeDocumentsProps {
  changeNumber: string;
  projectId: number;
  canWrite: boolean;
  onDocumentCountChange?: (count: number) => void;
}

interface Document {
  document_id: number;
  document_name: string;
  document_type: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  version: number;
  notes: string | null;
  created_at: string;
  uploaded_by_name?: string;
}

const DesignChangeDocuments: React.FC<DesignChangeDocumentsProps> = ({
  changeNumber,
  projectId,
  canWrite,
  onDocumentCountChange
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wordHtml, setWordHtml] = useState<string | null>(null);

  const [uploadData, setUploadData] = useState({
    document_type: 'drawing',
    notes: ''
  });

  const [editData, setEditData] = useState({
    document_name: '',
    notes: ''
  });

  const documentTypes = [
    { value: 'drawing', label: 'Drawing', icon: '📐', formats: 'pdf, dwg, dxf, jpg, jpeg, png' },
    { value: 'specification', label: 'Specification', icon: '📋', formats: 'pdf, doc, docx' },
    { value: 'correspondence', label: 'Correspondence', icon: '📧', formats: 'pdf, doc, docx, msg, eml' },
    { value: 'approval', label: 'Approval', icon: '✅', formats: 'pdf, jpg, jpeg, png' },
    { value: 'photo', label: 'Photo/Image', icon: '📷', formats: 'jpg, jpeg, png, gif, bmp' },
    { value: 'other', label: 'Other', icon: '📎', formats: 'pdf, doc, docx, xlsx, xls, jpg, jpeg, png, gif, zip, rar' }
  ];

  const fetchDocuments = useCallback(async (notifyParent: boolean = false) => {
    if (!changeNumber || !projectId) return;

    try {
      setLoading(true);
      const response = await fetch(
        apiUrl(`/api/design-changes/${encodeURIComponent(changeNumber)}/documents?project_id=${projectId}`),
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const docs = data.documents || [];
        setDocuments(docs);
        if (notifyParent) {
          onDocumentCountChange?.(docs.length);
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch documents');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [changeNumber, projectId, onDocumentCountChange]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
    setUploadData({
      document_type: 'drawing',
      notes: ''
    });
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || uploading) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', uploadData.document_type);
      if (uploadData.notes) formData.append('notes', uploadData.notes);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(
        apiUrl(`/api/design-changes/${encodeURIComponent(changeNumber)}/documents?project_id=${projectId}`),
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        await fetchDocuments(true);
        handleUploadDialogClose();
        setSuccess('Document uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload document');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      const filename = document.file_path.split('/').pop();
      const response = await fetch(
        apiUrl(`/api/design-changes/${encodeURIComponent(changeNumber)}/documents/${filename}?project_id=${projectId}`),
        { credentials: 'include' }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = document.document_name;
        window.document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      } else {
        throw new Error('Failed to download document');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      setDeletingId(documentId);
      const response = await fetch(
        apiUrl(`/api/design-changes/${encodeURIComponent(changeNumber)}/documents/${documentId}?project_id=${projectId}`),
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        await fetchDocuments(true);
        setSuccess('Document deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete document');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditOpen = (document: Document) => {
    setEditingDocument(document);
    setEditData({
      document_name: document.document_name || '',
      notes: document.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingDocument(null);
  };

  const handleEditSave = async () => {
    if (!editingDocument) return;

    try {
      setSaving(true);
      const response = await fetch(
        apiUrl(`/api/design-changes/${encodeURIComponent(changeNumber)}/documents/${editingDocument.document_id}?project_id=${projectId}`),
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editData)
        }
      );

      if (response.ok) {
        await fetchDocuments(true);
        handleEditClose();
        setSuccess('Document updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update document');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.includes('pdf')) return <PdfIcon sx={{ color: '#D32F2F' }} />;
    if (mimeType?.includes('image')) return <ImageIcon sx={{ color: '#1976D2' }} />;
    if (mimeType?.includes('word') || mimeType?.includes('document')) return <DocIcon sx={{ color: '#1565C0' }} />;
    return <FileIcon sx={{ color: '#757575' }} />;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      drawing: '#2196F3',
      specification: '#9C27B0',
      correspondence: '#FF9800',
      approval: '#4CAF50',
      photo: '#E91E63',
      other: '#607D8B'
    };
    return colors[type] || '#607D8B';
  };

  const isPreviewable = (mimeType: string | null) => {
    if (!mimeType) return false;
    return (
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || // .docx
      mimeType === 'application/msword' // .doc
    );
  };

  const handlePreview = async (document: Document) => {
    try {
      setPreviewLoading(true);
      setPreviewDocument(document);
      setPreviewOpen(true);
      setWordHtml(null);
      setPreviewUrl(null);

      const filename = document.file_path.split('/').pop();
      const response = await fetch(
        apiUrl(`/api/design-changes/${encodeURIComponent(changeNumber)}/documents/${filename}?project_id=${projectId}`),
        { credentials: 'include' }
      );

      if (response.ok) {
        const blob = await response.blob();

        // Check if it's a Word document
        const isWord = document.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                       document.mime_type === 'application/msword';

        if (isWord) {
          // Convert Word to HTML using mammoth
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setWordHtml(result.value);
        } else {
          // For PDF and images, use blob URL
          const url = window.URL.createObjectURL(blob);
          setPreviewUrl(url);
        }
      } else {
        throw new Error('Failed to load preview');
      }
    } catch (err: any) {
      setError(err.message);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
    setWordHtml(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  if (loading && documents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'grey.50',
            cursor: 'pointer'
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <AttachFileIcon sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Attachments
            </Typography>
            <Badge badgeContent={documents.length} color="primary" sx={{ ml: 1 }}>
              <Box />
            </Badge>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {canWrite && (
              <Button
                size="small"
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadDialogOpen();
                }}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Upload
              </Button>
            )}
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Divider />

          {/* Alerts */}
          {error && (
            <Alert
              severity="error"
              sx={{ m: 2, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Fade in>
              <Alert
                severity="success"
                sx={{ m: 2, borderRadius: 2 }}
              >
                {success}
              </Alert>
            </Fade>
          )}

          {/* Documents Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>File</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Uploaded</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <DocumentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary" variant="body2">
                        No documents attached yet
                      </Typography>
                      {canWrite && (
                        <Button
                          size="small"
                          startIcon={<UploadIcon />}
                          onClick={handleUploadDialogOpen}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Upload your first document
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow
                      key={doc.document_id}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        '&:last-child td': { borderBottom: 0 }
                      }}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getFileIcon(doc.mime_type)}
                          <Box>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                              {doc.document_name}
                            </Typography>
                            {doc.notes && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                                {doc.notes}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={doc.document_type}
                          size="small"
                          sx={{
                            bgcolor: `${getTypeColor(doc.document_type)}15`,
                            color: getTypeColor(doc.document_type),
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(doc.file_size)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(doc.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          {isPreviewable(doc.mime_type) && (
                            <Tooltip title="Preview">
                              <IconButton
                                onClick={() => handlePreview(doc)}
                                size="small"
                                color="info"
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Download">
                            <IconButton
                              onClick={() => handleDownload(doc)}
                              size="small"
                              color="primary"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canWrite && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  onClick={() => handleEditOpen(doc)}
                                  size="small"
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDelete(doc.document_id)}
                                  size="small"
                                  color="error"
                                  disabled={deletingId === doc.document_id}
                                >
                                  {deletingId === doc.document_id ? (
                                    <CircularProgress size={18} color="error" />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </CardContent>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <UploadIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Upload Document</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {/* Drag & Drop Zone */}
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: dragActive ? 'primary.50' : 'grey.50',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }
              }}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                hidden
                onChange={handleFileSelect}
              />
              {selectedFile ? (
                <Box>
                  <FileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body1" fontWeight={500}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    Drag & drop file here or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    PDF, Images, Word, Excel supported (max 10MB)
                  </Typography>
                </Box>
              )}
            </Box>

            {uploading && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ borderRadius: 1, height: 6 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            <TextField
              select
              label="Document Type"
              value={uploadData.document_type}
              onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
              fullWidth
              size="small"
              helperText={`Supported formats: ${documentTypes.find(t => t.value === uploadData.document_type)?.formats || ''}`}
            >
              {documentTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{type.icon}</span>
                    {type.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Notes (optional)"
              value={uploadData.notes}
              onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Add any notes or description..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleUploadDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Edit Document</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Document Name"
              value={editData.document_name}
              onChange={(e) => setEditData({ ...editData, document_name: e.target.value })}
              fullWidth
              size="small"
            />

            <TextField
              label="Notes"
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="Add any notes or description..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleEditClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PreviewIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {previewDocument?.document_name || 'Document Preview'}
            </Typography>
          </Box>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'grey.100' }}>
          {previewLoading ? (
            <CircularProgress />
          ) : wordHtml ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
                p: 4,
                bgcolor: 'white'
              }}
              dangerouslySetInnerHTML={{ __html: wordHtml }}
            />
          ) : previewUrl ? (
            previewDocument?.mime_type?.startsWith('image/') ? (
              <Box
                component="img"
                src={previewUrl}
                alt={previewDocument?.document_name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            ) : previewDocument?.mime_type === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                title={previewDocument?.document_name}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            ) : (
              <Typography color="text.secondary">
                Preview not available for this file type
              </Typography>
            )
          ) : (
            <Typography color="text.secondary">
              Loading preview...
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleClosePreview}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => previewDocument && handleDownload(previewDocument)}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DesignChangeDocuments;
